/**
 * Contract of `GET {scholaraniumURL}/cementing/{subject}/status` and
 * `POST {scholaraniumURL}/cementing/module/{categoryCorrelationId}/choice`
 * (escholar `CementingController` / `CementingStatusVO`, spec section 12).
 *
 * The server carries no presentation vocabulary: bars and scores are correct
 * answers, percentages are for display only, and every label, button and
 * recommendation the course page shows is derived here.
 */

export type CementingMode = 'FIXED' | 'LEGACY';
export type CementingDifficulty = 'MEDIUM' | 'HARD';
export type CementingModuleState =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'THRESHOLD_MET_GOING_FOR_MASTERY'
  | 'DONE_MASTERY_MET'
  | 'DONE_THRESHOLD_MET'
  | 'DONE_THRESHOLD_NOT_MET'
  | 'NOT_NEEDED';
export type CementingStateReason = 'CHOSEN' | 'MASTERY_REACHED' | 'QUIZZES_USED_UP' | 'HARD_PASSED';
export type CementingLevel = 'BELOW_THRESHOLD' | 'THRESHOLD_MET' | 'MASTERY_MET';
export type CementingMomentKind =
  | 'WARMUP_DONE'
  | 'FIRST_CHECKIN_NEXT'
  | 'SILENT'
  | 'CELEBRATE_THRESHOLD'
  | 'CELEBRATE_MASTERY'
  | 'CHECKIN_1'
  | 'CHECKIN_BELOW_AGAIN'
  | 'CHECKIN_MASTERY'
  | 'FINAL'
  | 'PRACTICE'
  | 'ABANDONED';
export type CementingChoice = 'KEEP_GOING' | 'STOP';
export type CementingAttemptStatus = 'COMPLETED' | 'INPROGRESS' | 'ABANDONED';
export type CementingAttemptKind = 'WARMUP' | 'COUNTED' | 'PRACTICE' | 'ABANDONED' | 'IN_PROGRESS' | 'NOT_STARTED' | 'RETAKE';
export type CementingTargetKind = 'AFTER_WARMUP' | 'PAIR' | 'OUT_OF_REACH';
export type CementingBarName = 'THRESHOLD' | 'MASTERY';
/** Dates arrive as ISO strings or epoch millis depending on the Jackson setup. */
export type ApiDate = string | number | null;

/** A bar as correct answers, display percent and mistakes allowed (spec section 10). */
export interface CementingBar {
  score: number;
  percent: number;
  mistakesAllowed: number;
  /** Half the allowance, rounded down: the per-quiz aim when no pair exists yet. */
  perQuizMistakes: number;
}

export interface CementingRules {
  questionsPerQuiz: number;
  l20Max: number;
  quizzesInModule: number;
  threshold: CementingBar;
  masteryTarget: CementingBar;
  thresholdPct: number | null;
  maxGapPoints: number | null;
  relaxedRecommendedQuiz: number;
  /** Hard only: warm-up percent that softens the Hard warning; null on Medium. */
  hardWarmupPct: number | null;
}

export interface CementingL20 {
  score: number;
  l20Max: number;
  percent: number;
  mistakes: number;
  pairEven: boolean | null;
  prevQuizNumber: number | null;
  prevScore: number | null;
  quizNumber: number | null;
  quizScore: number | null;
  farBelowQuizNumber: number | null;
}

export interface CementingTarget {
  kind: CementingTargetKind;
  quizNumber: number | null;
  bar: CementingBarName;
  barScore: number;
  prevScore: number | null;
  targetScore: number | null;
  mistakesOrFewer: number | null;
  reachable: boolean;
  perQuizMistakes: number;
  /** Questions the next quiz is expected to have (a legacy quiz is as long as the last one). */
  questions?: number | null;
}

export interface CementingSummary {
  status: CementingModuleState;
  stateReason: CementingStateReason | null;
  stateQuizNumber: number | null;
  stateQuizId: number | null;
  open: boolean;
  bestLevel: CementingLevel;
  countsAsComplete: boolean;
  canTakeQuiz: boolean;
  practice: boolean;
  countedQuizzes: number;
  abandonedQuizzes: number;
  quizzesLeft: number;
  quizzesTotal: number;
  checkinsSoFar: number;
  warmupScore: number | null;
  latestL20: CementingL20 | null;
  nextCheckinQuizNumber: number | null;
  nextCheckinIsFinal: boolean;
  nextQuizTarget: CementingTarget | null;
  /** Quiz number of the moment whose buttons are active; null when nothing is waiting. */
  waitingQuizNumber: number | null;
}

export interface CementingMoment {
  quizId: number | null;
  quizNumber: number;
  countedPosition: number | null;
  score: number | null;
  prevQuizNumber: number | null;
  prevScore: number | null;
  l20: number | null;
  l20Max: number;
  mistakes: number | null;
  moment: CementingMomentKind | null;
  takesChoice: boolean;
  allowsStop: boolean;
  active: boolean;
  uneven: boolean;
  unevenWouldBe: CementingLevel | null;
  pairEven: boolean | null;
  relaxedInPair: boolean | null;
  newLevel: CementingLevel | null;
  statusAfter: CementingModuleState | null;
  farBelowQuizNumber: number | null;
  choice: CementingChoice | null;
  choiceHow: 'PRESSED' | 'INFERRED' | null;
  choiceAt: ApiDate;
  createdAt: ApiDate;
}

export interface CementingNextQuiz {
  quizNumber: number;
  /** Template correlation id (fixed path; null on legacy). */
  correlationId: string | null;
  maxTimeSeconds: number | null;
  relaxedMaxTimeSeconds: number | null;
  recommendedTiming: 'RELAXED' | 'STANDARD' | null;
  practice: boolean;
  inProgressQuizId: number | null;
  resumable: boolean;
  resumeUntil: ApiDate;
  /** Questions answered so far in the unfinished quiz; null when none is in progress. */
  answeredQuestions?: number | null;
}

export interface CementingAttempt {
  quizNumber: number;
  correlationId: string | null;
  status: CementingAttemptStatus | null;
  quizId: number | null;
  kind: CementingAttemptKind;
  score: number | null;
  questions: number | null;
  mistakes: number | null;
  accuracyPercent: number | null;
  startedAt: ApiDate;
  finishedAt: ApiDate;
  timing: 'RELAXED' | 'STANDARD' | null;
  recommendedTiming: 'RELAXED' | 'STANDARD' | null;
  endMode: 'FINISHED' | 'TIMED_OUT' | 'ABANDONED' | null;
  retakes: CementingAttempt[];
}

export interface CementingQuizBreakdown {
  quizId: number;
  quizNumber: number;
  buckets: { [bucket: string]: number };
}

export interface CementingCategory {
  categoryKey: string;
  /** What the choice endpoint takes. */
  correlationId: string;
  difficulty: CementingDifficulty;
  name: string;
  sequence: number;
  rules: CementingRules;
  summary: CementingSummary;
  nextQuiz: CementingNextQuiz | null;
  moments: CementingMoment[];
  /** One slot per quiz number of the module, in order. */
  quizzes: CementingAttempt[];
  l20MistakeBreakdown: { [bucket: string]: number };
  l20MistakeBreakdownByQuiz: CementingQuizBreakdown[];
}

export interface CementingStatus {
  subject: string;
  mode: CementingMode | null;
  categories: CementingCategory[];
}

/* ------------------------------------------------------------------------ */
/* Small helpers                                                             */
/* ------------------------------------------------------------------------ */

/** "Watch the Loop" video shown on the fix-it steps and the Help pop-up; empty until the video exists. */
export const CEMENTING_LOOP_VIDEO_URL = 'https://www.loom.com/share/9a5eba71aa71460da8d8a1ab081a8a19';

export const PASSED_STATES: CementingModuleState[] = ['THRESHOLD_MET_GOING_FOR_MASTERY', 'DONE_MASTERY_MET', 'DONE_THRESHOLD_MET'];
const CHECKIN_MOMENTS: CementingMomentKind[] = ['CHECKIN_1', 'CHECKIN_BELOW_AGAIN', 'CHECKIN_MASTERY'];
const BOUNDARY_MOMENTS: CementingMomentKind[] = [...CHECKIN_MOMENTS, 'CELEBRATE_THRESHOLD', 'CELEBRATE_MASTERY', 'FINAL'];

export function toDate(value: ApiDate | undefined): Date | null {
  if (value === null || value === undefined || value === '') { return null; }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function plural(n: number, one: string, many?: string): string {
  return n + ' ' + (n === 1 ? one : (many || one + 's'));
}

export function moduleName(category: CementingCategory | null | undefined): string {
  if (!category) { return 'Cementing'; }
  return (category.difficulty === 'HARD' ? 'Hard' : 'Medium') + ' Cementing';
}

/** Journey learning-level short name → cementing difficulty. */
export const CEMENTING_LEVEL_DIFFICULTY: { [shortName: string]: CementingDifficulty } = { CM: 'MEDIUM', CH: 'HARD' };

export function findCategory(status: CementingStatus | null, difficulty: CementingDifficulty): CementingCategory | null {
  if (!status || !status.categories) { return null; }
  return status.categories.find(c => c.difficulty === difficulty) || null;
}

/**
 * Module-view topics carry only a name. A topic is treated as a cementing stage
 * when its name mentions cementing and a difficulty, e.g. "Stage 2 - Medium Cementing".
 */
export function cementingDifficultyOfTopicName(name: string | null | undefined): CementingDifficulty | null {
  if (!name || !/cement/i.test(name)) { return null; }
  if (/\bmed(ium)?\b/i.test(name)) { return 'MEDIUM'; }
  if (/\bhard\b/i.test(name)) { return 'HARD'; }
  return null;
}

export function momentOfQuiz(category: CementingCategory, quizNumber: number): CementingMoment | null {
  const list = (category.moments || []).filter(m => m.quizNumber === quizNumber);
  return list.length ? list[list.length - 1] : null;
}

export function attemptedSlots(category: CementingCategory): CementingAttempt[] {
  return (category.quizzes || []).filter(q => !!q.status);
}

export function attemptDate(a: CementingAttempt): Date | null {
  return toDate(a.finishedAt) || toDate(a.startedAt);
}

function dayDiff(a: Date | null, b: Date | null): number | null {
  if (!a || !b) { return null; }
  const da = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const db = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((db - da) / 86400000);
}

/** Questions in a quiz of the module: its own count (legacy RC and MSR quizzes run 5 to 10), else the module's. */
export function questionsOf(category: CementingCategory, quizNumber: number | null | undefined): number {
  const slot = (category.quizzes || []).find(q => q.quizNumber === quizNumber);
  return slot && slot.questions ? slot.questions : (category.rules.questionsPerQuiz || 10);
}

/**
 * A bar as it applies to a pair of `max` questions. Bars are stored out of
 * rules.l20Max; a legacy RC / MSR pair can be shorter (6 + 6 = 12, not 18).
 * Same rule as the server: the smallest score at the bar's percent.
 */
export function barFor(bar: CementingBar, rules: CementingRules, max: number | null | undefined): CementingBar {
  if (!max || !rules.l20Max || max === rules.l20Max) { return bar; }
  const score = Math.floor((bar.score * max + rules.l20Max - 1) / rules.l20Max);
  const mistakesAllowed = Math.max(0, max - score);
  return { score, percent: bar.percent, mistakesAllowed, perQuizMistakes: Math.floor(mistakesAllowed / 2) };
}

/** Level of an L20 score against the module bars, for a pair of `max` questions (rules.l20Max when omitted). */
export function levelOf(score: number | null, rules: CementingRules, max?: number | null): CementingLevel | null {
  if (score === null || score === undefined) { return null; }
  if (score >= barFor(rules.masteryTarget, rules, max).score) { return 'MASTERY_MET'; }
  if (score >= barFor(rules.threshold, rules, max).score) { return 'THRESHOLD_MET'; }
  return 'BELOW_THRESHOLD';
}

export type Tone = 'blue' | 'red' | 'amber' | 'green' | 'grey';

export function levelTone(level: CementingLevel | null): Tone {
  switch (level) {
    case 'MASTERY_MET': return 'green';
    case 'THRESHOLD_MET': return 'amber';
    case 'BELOW_THRESHOLD': return 'red';
    default: return 'grey';
  }
}

export type PillIcon = 'play' | 'down' | 'up' | 'up-right' | 'check' | 'star' | 'flag' | 'x' | 'none' | 'info' | 'mentor' | 'redo';

/* ------------------------------------------------------------------------ */
/* Plan view                                                                 */
/* ------------------------------------------------------------------------ */

export type TileKind = 'done' | 'abandoned' | 'inprogress' | 'next' | 'future' | 'extra';

export interface PlanTile {
  quizNumber: number;
  kind: TileKind;
  score: string | null;
  date: string | null;
  gapDays: number | null;
  retakes: number;
  attempt: CementingAttempt | null;
  /** Dashed outline: a quiz beyond the planned module size. */
  extra: boolean;
  /** In-progress quiz: questions answered of the quiz's total, for the Resume tile's bar. */
  progress?: { answered: number; total: number } | null;
}

export interface PlanGroup {
  label: string;
  tiles: PlanTile[];
  /** Line under the group: what happened at its check-in, or the next check-in. */
  status: { text: string; icon: 'flag' | 'star' | 'none'; strong: boolean; tone: Tone } | null;
  /** Connector drawn after this group. */
  connector: 'flag' | 'star' | 'none';
}

export function formatDay(d: Date | null): string | null {
  if (!d) { return null; }
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return d.getDate() + ' ' + months[d.getMonth()];
}

/** Short label under a plan group: what happened at its moment. */
export function choiceLabel(moment: CementingMoment): string {
  const kind = moment.moment;
  if (kind === 'CELEBRATE_MASTERY') { return 'Mastery reached'; }
  if (kind === 'FINAL') { return 'All quizzes used'; }
  if (moment.choice === 'STOP') { return kind === 'CHECKIN_BELOW_AGAIN' ? 'Done: Threshold not met' : 'Done: Threshold met'; }
  if (moment.choice === 'KEEP_GOING') {
    return kind === 'CELEBRATE_THRESHOLD' ? 'Going for mastery' : kind === 'CHECKIN_MASTERY' ? 'Kept going for mastery'
      : kind === 'CHECKIN_1' ? 'Fixed your mistakes, kept going' : 'Chose to keep going';
  }
  if (kind === 'CHECKIN_1') { return 'Not there yet'; }
  return 'Your choice';
}

/** Past card of a moment (spec 8.3): pressed and inferred outcomes read the same. */
export function pastCardText(moment: CementingMoment): string | null {
  const kind = moment.moment;
  if (!moment.takesChoice || !moment.choice) { return null; }
  if (moment.choice === 'STOP') {
    return kind === 'CHECKIN_BELOW_AGAIN' ? 'Done: Threshold not met. Talk to your strategy mentor.' : 'Done: Threshold met.';
  }
  switch (kind) {
    case 'CELEBRATE_THRESHOLD': return 'You\x27re going for mastery.';
    case 'CHECKIN_1': return 'You fixed your mistakes and kept going.';
    case 'CHECKIN_MASTERY': return 'You kept going for mastery.';
    case 'CHECKIN_BELOW_AGAIN': return 'You chose to keep going.';
    default: return null;
  }
}

/** Answered / total of the quiz in progress, when the API says how far the student got. */
export function resumeProgress(category: CementingCategory, quizNumber: number): { answered: number; total: number } | null {
  const next = category.nextQuiz;
  if (!next || !next.inProgressQuizId || next.quizNumber !== quizNumber) { return null; }
  const answered = next.answeredQuestions;
  if (answered === null || answered === undefined) { return null; }
  const total = questionsOf(category, quizNumber);
  return { answered: Math.min(answered, total), total };
}

/** "4 of 10" for the quiz in progress, wrapped in the given text; empty when unknown. */
export function resumeText(category: CementingCategory, before: string = '', after: string = ''): string {
  const p = category.nextQuiz ? resumeProgress(category, category.nextQuiz.quizNumber) : null;
  return p ? before + p.answered + ' of ' + p.total + after : '';
}

export function derivePlan(category: CementingCategory): PlanGroup[] {
  const { summary, rules, nextQuiz } = category;
  const slots = (category.quizzes || []).slice().sort((a, b) => a.quizNumber - b.quizNumber);
  const groups: PlanGroup[] = [];
  let current: PlanTile[] = [];
  let previousDate: Date | null = null;
  let groupIndex = 0;
  const extraFrom = rules.quizzesInModule;

  const closeGroup = (closing: CementingMoment | null) => {
    if (!current.length) { return; }
    const first = current[0].quizNumber;
    const label = groupIndex === 0 ? 'Warm-up' : (first > extraFrom ? 'Extra' : 'Group ' + groupIndex);
    let status: PlanGroup['status'] = null;
    let connector: PlanGroup['connector'] = 'none';
    if (closing) {
      const text = choiceLabel(closing);
      const star = closing.moment === 'CELEBRATE_THRESHOLD' || closing.moment === 'CELEBRATE_MASTERY';
      status = { text, icon: star ? 'star' : (closing.moment === 'FINAL' ? 'none' : 'flag'), strong: true, tone: star ? 'amber' : 'grey' };
      connector = closing.moment === 'FINAL' ? 'none' : (star ? 'star' : 'flag');
    }
    groups.push({ label, tiles: current, status, connector });
    current = [];
    groupIndex++;
  };

  const practice: PlanTile[] = [];
  for (const slot of slots) {
    if (!slot.status) { continue; }
    const date = attemptDate(slot);
    const gap = dayDiff(previousDate, date);
    const tile: PlanTile = {
      quizNumber: slot.quizNumber,
      kind: slot.status === 'COMPLETED' ? 'done' : slot.status === 'ABANDONED' ? 'abandoned' : 'inprogress',
      score: slot.status === 'COMPLETED' && slot.accuracyPercent !== null ? slot.accuracyPercent + '%' : null,
      date: formatDay(date),
      gapDays: gap !== null && gap > 1 ? gap : null,
      retakes: (slot.retakes || []).length,
      attempt: slot,
      extra: slot.quizNumber > extraFrom,
      progress: slot.status === 'INPROGRESS' ? resumeProgress(category, slot.quizNumber) : null
    };
    if (date) { previousDate = date; }
    if (slot.kind === 'PRACTICE') { practice.push(tile); continue; }
    current.push(tile);
    const moment = momentOfQuiz(category, slot.quizNumber);
    const kind = moment ? moment.moment : null;
    if (slot.kind === 'WARMUP' && groupIndex === 0) { closeGroup(null); continue; }
    if (kind && BOUNDARY_MOMENTS.indexOf(kind) >= 0) { closeGroup(moment); }
  }

  if (summary.open && nextQuiz && !nextQuiz.practice) {
    // Nothing taken yet: the next quiz is the warm-up, a group of its own, and the first check-in
    // comes two quizzes after it (the server sends no check-in before the first attempt).
    // An in-progress warm-up is the same: its Resume tile is the Warm-up group.
    const warmupNext = groupIndex === 0 && (!current.length || (current.length === 1 && current[0].kind === 'inprogress'));
    let start = nextQuiz.quizNumber;
    if (warmupNext) {
      const tiles: PlanTile[] = current.length ? current
        : [{ quizNumber: start, kind: 'next', score: null, date: null, gapDays: null, retakes: 0, attempt: null, extra: start > extraFrom }];
      groups.push({ label: 'Warm-up', tiles, status: null, connector: 'none' });
      current = [];
      groupIndex++;
      start = tiles[0].quizNumber + 1;
    }
    // A quiz in progress already has its (Resume) tile: the next one is not added again.
    const resumeTileShown = !warmupNext && current.some(t => t.quizNumber === start);
    if (resumeTileShown) { start++; }
    // The group in progress runs up to the next check-in; its first unused quiz is "next".
    const checkin = summary.nextCheckinQuizNumber;
    const end = checkin && checkin >= start ? checkin : (warmupNext ? start + 1 : (resumeTileShown ? start - 1 : start));
    const maxNumber = slots.length ? slots[slots.length - 1].quizNumber : end;
    for (let n = start; n <= Math.min(end, maxNumber); n++) {
      const extra = n > extraFrom;
      current.push({ quizNumber: n, kind: n === nextQuiz.quizNumber ? 'next' : (extra ? 'extra' : 'future'), score: null, date: null, gapDays: null, retakes: 0, attempt: null, extra });
    }
    if (current.length) {
      const first = current[0].quizNumber;
      groups.push({
        label: first > extraFrom ? 'Extra' : 'Group ' + Math.max(1, groupIndex),
        tiles: current,
        status: { text: 'Next check-in', icon: 'flag', strong: false, tone: 'grey' },
        connector: 'none'
      });
      current = [];
      groupIndex++;
    }
    // Before the first check-in the plan previews the following pair.
    if (summary.checkinsSoFar === 0 && end < maxNumber) {
      const preview: PlanTile[] = [];
      for (let n = end + 1; n <= Math.min(end + 2, maxNumber); n++) {
        const extra = n > extraFrom;
        preview.push({ quizNumber: n, kind: extra ? 'extra' : 'future', score: null, date: null, gapDays: null, retakes: 0, attempt: null, extra });
      }
      groups.push({ label: preview[0].quizNumber > extraFrom ? 'Extra' : 'Group ' + groupIndex, tiles: preview, status: null, connector: 'none' });
    }
  } else if (current.length) {
    closeGroup(null);
  }

  if (nextQuiz && nextQuiz.practice && !nextQuiz.inProgressQuizId) {
    practice.push({ quizNumber: nextQuiz.quizNumber, kind: 'next', score: null, date: null, gapDays: null, retakes: 0, attempt: null, extra: nextQuiz.quizNumber > extraFrom });
  }
  if (practice.length) {
    groups.push({ label: 'After cementing', tiles: practice, status: { text: 'Practice', icon: 'none', strong: false, tone: 'grey' }, connector: 'none' });
  }
  return groups;
}

/* ------------------------------------------------------------------------ */
/* Chart view                                                                */
/* ------------------------------------------------------------------------ */

export interface CementingL20Like {
  score: number;
  l20Max: number;
  percent: number;
  mistakes: number;
  prevQuizNumber: number;
  quizNumber: number;
  level: CementingLevel;
  /** Distance line: "1 mistake from the threshold" / "1 mistake from mastery". */
  distance: string;
}

export interface PairBreakdown {
  quizzes: { quizNumber: number; squares: string[]; mistakes: number }[];
  mistakes: { bucket: string; label: string; color: string; count: number }[];
  correct: number;
}

export interface ChartColumn {
  quizNumber: number;
  label: string;
  sub: string | null;
  attempt: CementingAttempt | null;
  moment: CementingMoment | null;
  /** L20 after this quiz as a percent; null = "no L20" / abandoned / future. */
  l20Percent: number | null;
  l20: CementingL20Like | null;
  noL20Text: string | null;
  practice: boolean;
  /** The pair behind the L20 spans more than one day. */
  split: boolean;
  /** Days since the previous attempted quiz, when more than one. */
  gapDays: number | null;
  marker: 'checkin' | 'star' | 'none';
  future: boolean;
  next: boolean;
  /** Breakdown squares, latest pair only. */
  breakdown: PairBreakdown | null;
}

export interface ChartView {
  columns: ChartColumn[];
  thresholdPercent: number;
  masteryPercent: number;
  /** Index after which the "Cementing done" line is drawn, or null. */
  doneAfterIndex: number | null;
  /** Index of the dashed "next check-in" line, or null. */
  nextCheckinIndex: number | null;
  intro: string;
}

export const BUCKETS: { key: string; label: string; color: string; mistake: boolean }[] = [
  { key: 'JUDGEMENT', label: 'Judgment gap', color: '#F9D48E', mistake: true },
  { key: 'SPEED', label: 'Speed gap', color: '#F6BE9A', mistake: true },
  { key: 'ABILITY', label: 'Ability gap', color: '#F2A7A6', mistake: true },
  { key: 'CASCADE', label: 'Cascade', color: '#C9C9C9', mistake: true },
  { key: 'SCORE_BOOSTER', label: 'Score booster', color: '#BFDBFE', mistake: false },
  { key: 'MASTERY', label: 'Correct', color: '#A3E0CC', mistake: false },
  { key: 'NOT_REACHED', label: 'Not reached', color: '#E5E7EB', mistake: true }
];

function distanceText(score: number, rules: CementingRules, max: number): string {
  const level = levelOf(score, rules, max);
  if (level === 'MASTERY_MET') { return 'Mastery'; }
  if (level === 'THRESHOLD_MET') { return plural(barFor(rules.masteryTarget, rules, max).score - score, 'mistake') + ' from mastery'; }
  return plural(barFor(rules.threshold, rules, max).score - score, 'mistake') + ' from the threshold';
}

/** The buckets behind one L20 bar: its own two quizzes, added up (every counted quiz is served, spec 12). */
function breakdownOf(category: CementingCategory, prevNumber: number, number: number): PairBreakdown | null {
  const byQuiz = category.l20MistakeBreakdownByQuiz || [];
  if (byQuiz.length < 2) { return null; }
  const numbers = byQuiz.map(b => b.quizNumber);
  if (numbers.indexOf(prevNumber) < 0 || numbers.indexOf(number) < 0) { return null; }
  const quizzes = [prevNumber, number].map(n => {
    const q = byQuiz.find(b => b.quizNumber === n);
    const squares: string[] = [];
    let mistakes = 0;
    BUCKETS.filter(b => !b.mistake).concat(BUCKETS.filter(b => b.mistake)).forEach(b => {
      const count = q && q.buckets ? (q.buckets[b.key] || 0) : 0;
      if (b.mistake) { mistakes += count; }
      for (let i = 0; i < count; i++) { squares.push(b.color); }
    });
    return { quizNumber: n, squares, mistakes };
  });
  const total: { [bucket: string]: number } = {};
  [prevNumber, number].forEach(n => {
    const q = byQuiz.find(b => b.quizNumber === n);
    Object.keys((q && q.buckets) || {}).forEach(k => total[k] = (total[k] || 0) + (q.buckets[k] || 0));
  });
  const mistakes = BUCKETS.filter(b => b.mistake).map(b => ({ bucket: b.key, label: b.label, color: b.color, count: total[b.key] || 0 }))
    .filter(m => m.count > 0 || ['JUDGEMENT', 'SPEED', 'ABILITY', 'CASCADE'].indexOf(m.bucket) >= 0);
  const correct = BUCKETS.filter(b => !b.mistake).reduce((sum, b) => sum + (total[b.key] || 0), 0);
  return { quizzes, mistakes, correct };
}

export function deriveChart(category: CementingCategory): ChartView {
  const { summary, rules, nextQuiz } = category;
  const slots = (category.quizzes || []).slice().sort((a, b) => a.quizNumber - b.quizNumber);
  const columns: ChartColumn[] = [];
  let previousDate: Date | null = null;
  let previousCountedDate: Date | null = null;
  let countedSeen = 0;
  let doneAfterIndex: number | null = null;
  for (const slot of slots) {
    if (!slot.status) { continue; }
    const moment = momentOfQuiz(category, slot.quizNumber);
    const date = attemptDate(slot);
    const gap = dayDiff(previousDate, date);
    const counted = slot.status === 'COMPLETED';
    const col: ChartColumn = {
      quizNumber: slot.quizNumber,
      label: 'Quiz ' + slot.quizNumber,
      sub: formatDay(date),
      attempt: slot,
      moment,
      l20Percent: null,
      l20: null,
      noL20Text: null,
      practice: slot.kind === 'PRACTICE',
      split: false,
      gapDays: gap !== null && gap > 1 ? gap : null,
      marker: moment && CHECKIN_MOMENTS.indexOf(moment.moment) >= 0 ? 'checkin'
        : moment && (moment.moment === 'CELEBRATE_THRESHOLD' || moment.moment === 'CELEBRATE_MASTERY') ? 'star' : 'none',
      future: false,
      next: false,
      breakdown: null
    };
    if (counted) {
      countedSeen++;
      if (moment && moment.l20 !== null && moment.l20 !== undefined && moment.prevQuizNumber) {
        const score = moment.l20;
        const max = moment.l20Max || rules.l20Max;
        col.l20Percent = Math.round(score * 100 / max);
        col.l20 = {
          score, l20Max: max, percent: col.l20Percent,
          mistakes: max - score,
          prevQuizNumber: moment.prevQuizNumber, quizNumber: slot.quizNumber,
          level: levelOf(score, rules, max), distance: distanceText(score, rules, max)
        };
        const pairGap = dayDiff(previousCountedDate, date);
        col.split = pairGap !== null && pairGap > 1;
        col.breakdown = breakdownOf(category, moment.prevQuizNumber, slot.quizNumber);
      } else {
        col.noL20Text = countedSeen < 3 ? 'no L20' : null;
      }
      previousCountedDate = date;
    } else if (slot.status === 'ABANDONED') {
      col.noL20Text = 'exited';
    } else if (nextQuiz && nextQuiz.inProgressQuizId && nextQuiz.quizNumber === slot.quizNumber) {
      col.next = true;                        // the quiz to go back to: amber label box, "resume"
      col.sub = 'resume';
    } else {
      col.noL20Text = 'in progress';
    }
    if (date) { previousDate = date; }
    columns.push(col);
    if (!summary.open && summary.stateQuizNumber === slot.quizNumber) { doneAfterIndex = columns.length - 1; }
  }
  let nextCheckinIndex: number | null = null;
  if (nextQuiz && nextQuiz.inProgressQuizId && summary.open && summary.nextCheckinQuizNumber === nextQuiz.quizNumber) {
    const i = columns.findIndex(col => col.quizNumber === nextQuiz.quizNumber);
    nextCheckinIndex = i >= 0 ? i : null;
  }
  if (nextQuiz && !nextQuiz.inProgressQuizId) {
    const end = summary.open && summary.nextCheckinQuizNumber && summary.nextCheckinQuizNumber >= nextQuiz.quizNumber
      ? summary.nextCheckinQuizNumber : nextQuiz.quizNumber;
    const maxNumber = slots.length ? slots[slots.length - 1].quizNumber : end;
    for (let n = nextQuiz.quizNumber; n <= Math.min(end, maxNumber); n++) {
      columns.push({
        quizNumber: n, label: 'Quiz ' + n, sub: n === nextQuiz.quizNumber ? 'next' : null, attempt: null, moment: null,
        l20Percent: null, l20: null, noL20Text: null, practice: nextQuiz.practice, split: false, gapDays: null,
        marker: 'none', future: true, next: n === nextQuiz.quizNumber, breakdown: null
      });
      if (summary.open && n === end && summary.nextCheckinQuizNumber === end) { nextCheckinIndex = columns.length - 1; }
    }
  }
  const left = summary.quizzesLeft;
  let intro: string;
  if (!nextQuiz) {
    intro = 'All quizzes taken.';
  } else if (nextQuiz.practice) {
    intro = 'Practice quizzes left: ' + left + ' (next: Quiz ' + nextQuiz.quizNumber + ').';
  } else {
    intro = 'Quizzes left: ' + left + ' (next: Quiz ' + nextQuiz.quizNumber + ').';
  }
  intro += ' Hover a point to see your L20 and where the mistakes came from.';
  return { columns, thresholdPercent: rules.threshold.percent, masteryPercent: rules.masteryTarget.percent, doneAfterIndex, nextCheckinIndex, intro };
}

/* ------------------------------------------------------------------------ */
/* Table view                                                                */
/* ------------------------------------------------------------------------ */

export interface TableRow {
  kind: 'attempt' | 'retake' | 'done' | 'next' | 'more';
  quizNumber: number | null;
  label: string;
  extra: boolean;
  date: string | null;
  gapDays: number | null;
  score: string | null;
  mistakes: string | null;
  l20: { percent: string; mistakes: string } | null;
  l20Text: string | null;
  happened: { text: string; icon: 'flag' | 'star' | 'exit' | 'none' } | null;
  attempt: CementingAttempt | null;
  /** "done" and "more" rows carry their sentence here. */
  note: string | null;
  checkinAfter: number | null;
  highlight: boolean;
  /** Next row only: the quiz is in progress, so its button resumes it. */
  resume?: boolean;
}

export function happenedText(moment: CementingMoment | null, attempt: CementingAttempt): { text: string; icon: 'flag' | 'star' | 'exit' | 'none' } | null {
  if (attempt.kind === 'RETAKE') { return { text: 'Retake. Doesn\'t count.', icon: 'none' }; }
  if (attempt.status === 'ABANDONED') { return { text: 'Exited before the end. Doesn\'t count.', icon: 'exit' }; }
  if (attempt.status === 'INPROGRESS') { return { text: 'In progress.', icon: 'none' }; }
  if (attempt.kind === 'PRACTICE') { return { text: 'Practice', icon: 'none' }; }
  if (attempt.kind === 'WARMUP') { return { text: 'Warm-up. Doesn\'t count.', icon: 'none' }; }
  if (!moment || !moment.moment) { return null; }
  const kind = moment.moment;
  switch (kind) {
    case 'CHECKIN_1':
      return { text: moment.choice ? 'Check-in 1. ' + pastCardText(moment) : 'Check-in 1: not there yet.', icon: 'flag' };
    case 'CHECKIN_BELOW_AGAIN':
    case 'CHECKIN_MASTERY':
      return { text: (kind === 'CHECKIN_MASTERY' ? 'Mastery check-in. ' : 'Check-in. ') + (moment.choice ? pastCardText(moment) : 'Your choice.'), icon: 'flag' };
    case 'CELEBRATE_THRESHOLD':
      return { text: 'Threshold met. ' + (moment.choice ? pastCardText(moment) : 'Your choice.'), icon: 'star' };
    case 'CELEBRATE_MASTERY':
      return { text: 'Mastery reached.', icon: 'star' };
    case 'FINAL':
      return { text: 'Last quiz. ' + (moment.newLevel === 'THRESHOLD_MET' ? 'Threshold met.' : moment.newLevel === 'MASTERY_MET' ? 'Mastery reached.' : 'All quizzes used.'), icon: 'none' };
    default:
      return null;
  }
}

export function doneLabel(category: CementingCategory): string {
  switch (category.summary.status) {
    case 'DONE_MASTERY_MET': return 'Done: Mastery reached';
    case 'DONE_THRESHOLD_MET': return 'Done: Threshold met';
    case 'DONE_THRESHOLD_NOT_MET': return 'Done: Threshold not met';
    case 'NOT_NEEDED': return 'Not needed: Hard passed';
    default: return '';
  }
}

export function deriveTable(category: CementingCategory, highlightPair: number[] | null = null): TableRow[] {
  const { summary, rules, nextQuiz } = category;
  const rows: TableRow[] = [];
  const entries: { attempt: CementingAttempt; parent: number | null; date: Date | null }[] = [];
  for (const slot of category.quizzes || []) {
    if (!slot.status) { continue; }
    // The quiz in progress is the amber next row (Resume), not an attempt row as well.
    if (slot.status === 'INPROGRESS' && nextQuiz && nextQuiz.inProgressQuizId && nextQuiz.quizNumber === slot.quizNumber) { continue; }
    entries.push({ attempt: slot, parent: null, date: attemptDate(slot) });
    for (const r of slot.retakes || []) { entries.push({ attempt: { ...r, kind: 'RETAKE' }, parent: slot.quizNumber, date: attemptDate(r) }); }
  }
  entries.sort((a, b) => {
    const ta = a.date ? a.date.getTime() : 0;
    const tb = b.date ? b.date.getTime() : 0;
    return ta - tb || a.attempt.quizNumber - b.attempt.quizNumber;
  });
  let previousDate: Date | null = null;
  for (const e of entries) {
    const a = e.attempt;
    const moment = e.parent === null ? momentOfQuiz(category, a.quizNumber) : null;
    const gap = dayDiff(previousDate, e.date);
    const l20 = moment && moment.l20 !== null && moment.l20 !== undefined
      ? { percent: Math.round(moment.l20 * 100 / (moment.l20Max || rules.l20Max)) + '%', mistakes: plural((moment.l20Max || rules.l20Max) - moment.l20, 'mistake') }
      : null;
    rows.push({
      kind: e.parent === null ? 'attempt' : 'retake',
      quizNumber: a.quizNumber,
      label: 'Quiz ' + a.quizNumber + (e.parent !== null ? ' retake' : ''),
      extra: e.parent === null && a.quizNumber > rules.quizzesInModule,
      date: formatDay(e.date),
      gapDays: e.parent === null && gap !== null && gap > 1 ? gap : null,
      score: a.status === 'COMPLETED' && a.accuracyPercent !== null ? a.accuracyPercent + '%' : (a.status === 'ABANDONED' ? '—' : null),
      mistakes: a.status === 'COMPLETED' && a.mistakes !== null ? String(a.mistakes) : (a.status === 'ABANDONED' ? '—' : null),
      l20,
      l20Text: l20 ? null : (e.parent !== null ? 'Doesn\'t count' : '—'),
      happened: happenedText(moment, a),
      attempt: a,
      note: null,
      checkinAfter: null,
      highlight: !!highlightPair && e.parent === null && highlightPair.indexOf(a.quizNumber) >= 0
    });
    if (e.date) { previousDate = e.date; }
    if (!summary.open && e.parent === null && summary.stateQuizNumber === a.quizNumber) {
      rows.push({
        kind: 'done', quizNumber: null, label: '', extra: false, date: null, gapDays: null, score: null, mistakes: null, l20: null, l20Text: null,
        happened: null, attempt: null,
        note: 'Cementing done' + (e.date ? ' on ' + formatDay(e.date) : '') + ': ' + doneLabel(category)
          + (nextQuiz && nextQuiz.practice ? '. Quizzes after this are practice: no check-ins.' : '.'),
        checkinAfter: null, highlight: false
      });
    }
  }
  if (nextQuiz) {
    const inProgress = !!nextQuiz.inProgressQuizId;
    rows.push({
      kind: 'next', quizNumber: nextQuiz.quizNumber, label: 'Quiz ' + nextQuiz.quizNumber, extra: nextQuiz.quizNumber > rules.quizzesInModule,
      date: null, gapDays: null, mistakes: null, l20: null, l20Text: null,
      score: inProgress ? 'In progress' + resumeText(category, ' · ', ' answered') : (nextQuiz.practice ? 'Next (practice)' : 'Next'),
      happened: null, attempt: null, note: null, resume: inProgress,
      checkinAfter: summary.open && summary.nextCheckinQuizNumber ? summary.nextCheckinQuizNumber : null,
      highlight: false
    });
    const more = Math.max(0, summary.quizzesLeft - 1);
    if (more > 0) {
      rows.push({
        kind: 'more', quizNumber: null, label: '', extra: false, date: null, gapDays: null, score: null, mistakes: null, l20: null, l20Text: null,
        happened: null, attempt: null, note: '+ ' + plural(more, 'more quiz', 'more quizzes') + ' available after Quiz ' + nextQuiz.quizNumber,
        checkinAfter: null, highlight: false
      });
    }
  }
  return rows;
}

